/**
 * GlobalErrorDialog
 *
 * 어디서도 처리되지 않은 예외와 rejected promise를 화면 모달로 띄운다.
 *
 * @why 이 앱의 실사용 환경은 태블릿·휴대폰이고, 거기에는 개발자 도구가 없다.
 * console.error로만 남기면 사용자는 "눌렀는데 아무 일도 안 일어난다"만 볼 수 있고
 * 우리는 아무 단서도 받지 못한다. 처리 경로가 없는 실패라도 최소한
 * "무엇이 터졌는지"는 기기 화면에 남아야 원인을 좁힐 수 있다.
 *
 * 도메인 화면이 자체적으로 처리하는 실패는 여기까지 오지 않는다.
 * 여기 걸리는 것은 전부 "우리가 예상하지 못한 것"이므로, 잡음이 아니라 신호다.
 */

import { useEffect, useState } from 'react'
import { logger } from '@/core/logger'
import { describeError, toErrorMessage } from '@/core/errorDiagnostics'
import { ErrorDialog } from './ErrorDialog'

interface CapturedError {
  message: string
  detail: string
}

export function GlobalErrorDialog() {
  const [captured, setCaptured] = useState<CapturedError | null>(null)

  useEffect(() => {
    /**
     * 첫 에러만 띄운다.
     *
     * @why 하나가 터지면 뒤이어 연쇄로 여러 개가 나는 일이 흔하다.
     * 모두 띄우면 사용자는 모달을 계속 닫아야 하고, 정작 최초 원인은 뒤에 묻힌다.
     * 이미 떠 있으면 새 에러는 로그로만 남긴다. (닫으면 다시 받을 수 있다)
     */
    const capture = (source: string, error: unknown) => {
      logger.error(`[GlobalErrorDialog] 처리되지 않은 오류 (${source})`, error)

      setCaptured((prev) =>
        prev
          ? prev
          : {
              message: toErrorMessage(error, '알 수 없는 오류가 발생했습니다'),
              detail: describeError(error, { source }),
            }
      )
    }

    const handleError = (event: ErrorEvent) => {
      // event.error가 비어 있는 경우(교차 출처 스크립트 등)에는 메시지라도 남긴다
      capture('error', event.error ?? event.message)
    }

    const handleRejection = (event: PromiseRejectionEvent) => {
      capture('unhandledrejection', event.reason)
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [])

  return (
    <ErrorDialog
      isOpen={captured !== null}
      onClose={() => setCaptured(null)}
      title="예기치 못한 오류"
      message={captured?.message ?? ''}
      {...(captured?.detail ? { details: captured.detail } : {})}
    />
  )
}
